// Thin TipTap rich-text editor wrapper compatible with React Hook Form <Controller>.
// Usage:
//   <Controller control={control} name="note" render={({ field }) => (
//     <RichTextEditor value={field.value} onChange={field.onChange} editable={true} />
//   )} />
//
// The editor emits HTML strings (TipTap's getHTML()) — same format as @kolkov/angular-editor.
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { Box, IconButton, Paper, Divider } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  editable?: boolean;
  placeholder?: string;
  minHeight?: number | string;
}

export function RichTextEditor({
  value,
  onChange,
  editable = true,
  minHeight = 213,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    editable,
    onUpdate({ editor: e }) {
      onChange(e.getHTML());
    },
  });

  // Keep editor in sync when `value` changes externally (e.g. form reset).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (current !== value) {
      editor.commands.setContent(value ?? '');
    }
  }, [editor, value]);

  // Sync editable flag.
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {editable && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, flexWrap: 'wrap' }}>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                editor?.chain().focus().toggleBold().run();
              }}
              color={editor?.isActive('bold') ? 'primary' : 'default'}
              aria-label="Bold"
            >
              <FormatBoldIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                editor?.chain().focus().toggleItalic().run();
              }}
              color={editor?.isActive('italic') ? 'primary' : 'default'}
              aria-label="Italic"
            >
              <FormatItalicIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                editor?.chain().focus().toggleBulletList().run();
              }}
              color={editor?.isActive('bulletList') ? 'primary' : 'default'}
              aria-label="Bullet list"
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                editor?.chain().focus().toggleOrderedList().run();
              }}
              color={editor?.isActive('orderedList') ? 'primary' : 'default'}
              aria-label="Ordered list"
            >
              <FormatListNumberedIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
        </>
      )}
      <Box
        sx={{
          minHeight,
          px: 1.5,
          py: 1,
          '& .ProseMirror': {
            outline: 'none',
            minHeight,
            '& p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  );
}
